import { FC, useCallback } from "react";
import { models } from "types/api/viewModels.ts";
import { AcknowledgementPriceFormat, AcknowledgementPriceFormatType } from "types/declarations/enums.ts";
import {
	OrderDetailsLineItemFinalPriceGrid,
	OrderDetailsLineItemModDescription,
	OrderDetailsLineItemModNumber,
	OrderDetailsLineItemModRow,
	OrderDetailsLineItemModSkuCode,
	OrderDetailsLineItemPromotionRowGrid,
	OrderDetailsLineItemRowContainer,
	OrderDetailsLineItemRowContent,
	OrderDetailsLineItemRowIcon,
	OrderDetailsLineItemRowModSku,
	OrderDetailsLineItemRowRow
} from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemGrid.styles.ts";
import {
	getFinalPriceRow,
	getModificationRow,
	getPromotionRow,
	normalizeGridRow,
	useItemRow,
	usePricingRow
} from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemRow.utils.tsx";
import { RequiredSpecificationLabel } from "components/NewOrders/AddLineItem/constants.ts";
import { CardInfo } from "components/OrderDetailsLineItem/components/CardInfo";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { isFinalPricePromotionApplied } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";

export const OrderDetailsLineItemRow: FC<{
	item: models["LineItemViewModel"];
	configuration:
		| models["OrderConfigurationViewModel"]
		| models["PendingOrderConfigurationViewModel"]
		| models["ValidatedOrderConfigurationViewModel"];
	orderDetail: models["OrderDetailViewModel"] | models["DraftOrderViewModel"];
	variant: "detail" | "replacement";
	isAcknowledgement: boolean;
	ackPermission: AcknowledgementPriceFormatType | undefined;
	ackPermissionOverride?: AcknowledgementPriceFormatType | null;
	disableDrawer?: boolean;
	handleOnClick?: (
		lineItem: models["LineItemViewModel"] | null,
		configuration?: models["OrderConfigurationViewModel"] | null
	) => void;
	billTo?: models["BillToViewModel"] | null;
	isNewQuoteOrder?: boolean;
}> = ({
	item,
	configuration,
	orderDetail,
	variant,
	isAcknowledgement,
	ackPermission,
	ackPermissionOverride,
	disableDrawer,
	handleOnClick,
	billTo,
	isNewQuoteOrder
}) => {
	const { modifications } = item;
	const handleOpenFlyout = useCallback(
		() => handleOnClick?.(item, configuration),
		[item, configuration, handleOnClick]
	);
	const { isNewQuoteReviewPage, isNewQuoteConfirmationPage } = useUserLocation();

	const gridHeaderRow = normalizeGridRow(
		useItemRow(
			item,
			isAcknowledgement,
			variant === "replacement",
			handleOpenFlyout,
			billTo,
			isNewQuoteOrder,
			ackPermissionOverride
		)
	);
	const gridPricingRow = normalizeGridRow(usePricingRow(item, billTo, ackPermissionOverride));

	const hasMods = modifications && modifications.length > 0;
	const hasRequiredDimensions = item.requiredDimensions && item.requiredDimensions.length > 0;
	const hasRequiredCategories = item.requiredCategories && item.requiredCategories.length > 0;
	const hasAnyCustomizations = hasMods || hasRequiredCategories || hasRequiredDimensions;
	const isRowClickable = variant === "detail" && !!handleOnClick && !disableDrawer;

	// easy way to remove the first column
	const renderGridRow = (rows: ReturnType<typeof normalizeGridRow>, skipFirst = false) =>
		rows.map((row) => (!skipFirst || row.gridColumn !== 0 ? row.content : null));

	return (
		<OrderDetailsLineItemRowContainer
			isClickable={isRowClickable}
			onClick={isRowClickable ? handleOpenFlyout : undefined}
		>
			<CardInfo
				status={"status" in orderDetail && item.status === orderDetail.status ? undefined : item.status}
				quantityBackordered={item.partInfo?.partsQuantityBackordered}
			/>
			{variant !== "replacement" && isRowClickable && <OrderDetailsLineItemRowIcon as={ChevronRightIcon} />}
			<OrderDetailsLineItemRowRow isReviewOrder={isNewQuoteReviewPage || isNewQuoteConfirmationPage}>
				{renderGridRow(gridHeaderRow)}
			</OrderDetailsLineItemRowRow>
			{hasAnyCustomizations && (
				<OrderDetailsLineItemRowContent>
					{hasMods &&
						modifications?.map((mod, index) => (
							<OrderDetailsLineItemModRow key={`${index} ${mod.lineItemNumber}`}>
								{renderGridRow(normalizeGridRow(getModificationRow(mod, isAcknowledgement)))}
							</OrderDetailsLineItemModRow>
						))}
					{hasRequiredDimensions &&
						item.requiredDimensions?.map((dim, index) => (
							<OrderDetailsLineItemRowModSku key={`${index} ${dim.sku}`}>
								<OrderDetailsLineItemModNumber />
								<OrderDetailsLineItemModSkuCode>{dim.description}</OrderDetailsLineItemModSkuCode>
								<OrderDetailsLineItemModDescription>
									{dim.isUnrestrictedValue ? `${dim.value}` : `${dim.value}"`}
								</OrderDetailsLineItemModDescription>
							</OrderDetailsLineItemRowModSku>
						))}
					{hasRequiredCategories &&
						item.requiredCategories?.map((cat, index) => (
							<OrderDetailsLineItemRowModSku key={`${index} ${cat.sku}`}>
								<OrderDetailsLineItemModNumber />
								<OrderDetailsLineItemModSkuCode>
									{RequiredSpecificationLabel}
									{cat.categoryName}
								</OrderDetailsLineItemModSkuCode>
								<OrderDetailsLineItemModDescription>{cat.sku}</OrderDetailsLineItemModDescription>
							</OrderDetailsLineItemRowModSku>
						))}
				</OrderDetailsLineItemRowContent>
			)}
			{hasAnyCustomizations && isAcknowledgement && !isNewQuoteOrder && (
				<OrderDetailsLineItemRowRow>{renderGridRow(gridPricingRow, true)}</OrderDetailsLineItemRowRow>
			)}
			{(ackPermission !== AcknowledgementPriceFormat.LIST || billTo?.cashInAdvance) && isAcknowledgement && (
				<OrderDetailsLineItemRowContent>
					{item.pricing?.promotions?.map((promotion) => (
						<OrderDetailsLineItemPromotionRowGrid key={promotion.promotionName}>
							{renderGridRow(normalizeGridRow(getPromotionRow(promotion)), true)}
						</OrderDetailsLineItemPromotionRowGrid>
					))}
					{isFinalPricePromotionApplied(item, true) && (
						<OrderDetailsLineItemFinalPriceGrid>
							{renderGridRow(normalizeGridRow(getFinalPriceRow(item.pricing?.finalNetPrice)), true)}
						</OrderDetailsLineItemFinalPriceGrid>
					)}
				</OrderDetailsLineItemRowContent>
			)}
		</OrderDetailsLineItemRowContainer>
	);
};
